import React from "react";
import Grid from "./Grid";
import PropTypes from "prop-types";

/**
 * HGrid (Horizontal Grid) - A responsive Grid component that arranges children horizontally on larger screens,
 * and automatically stacks them vertically (full-width) on small screens for better mobile responsiveness and WCAG 2.1 Level AA compliance (400% zoom).
 * 
 * Default responsive behavior:
 * - Extra small screens (xs): full-width items (xs={12}) - stacks vertically
 * - Small screens and up (sm, md, lg, xl): uses provided breakpoints for horizontal layout
 * 
 * This is a convenience wrapper that implements mobile-first responsive design pattern,
 * ensuring content is accessible at 400% zoom without horizontal scrolling or content truncation.
 * 

 */
const HGrid = React.forwardRef(({ children, item, xs, sm, md, lg, xl, ...props }, ref) => {
  // If this is a Grid item, apply responsive defaults
  if (item) {
    // Default: full-width on extra small screens (xs) for 400% zoom compliance
    // Use provided xs if explicitly set, otherwise default to 12 (full-width)
    const responsiveXs = xs !== undefined ? xs : 12;
    
    // Pass through all props with responsive xs default
    return (
      <Grid
        ref={ref}
        item
        xs={responsiveXs}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        {...props}
      >
        {children}
      </Grid>
    );
  }

  // If this is a Grid container, pass through all props
  return (
    <Grid ref={ref} container {...props}>
      {children}
    </Grid>
  );
});

HGrid.displayName = 'HGrid';

HGrid.propTypes = {
  /**
   * The content of the component
   */
  children: PropTypes.node,
  /**
   * If true, the component will have the flex container behavior
   */
  container: PropTypes.bool,
  /**
   * If true, the component will have the flex item behavior
   * When item=true, automatically applies xs={12} (full-width) unless xs is explicitly provided
   */
  item: PropTypes.bool,
  /**
   * Defines the space between the type item components
   */
  spacing: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      sm: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
    }),
  ]),
  /**
   * Defines the number of grids the component is going to use (xs breakpoint)
   * Defaults to 12 (full-width) when item=true for mobile-first responsive design
   */
  xs: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (sm breakpoint)
   */
  sm: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (md breakpoint)
   */
  md: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (lg breakpoint)
   */
  lg: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the number of grids the component is going to use (xl breakpoint)
   */
  xl: PropTypes.oneOfType([
    PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, "auto", true]),
    PropTypes.bool,
  ]),
  /**
   * Defines the flex-direction style property
   */
  direction: PropTypes.oneOf(["row", "row-reverse", "column", "column-reverse"]),
  /**
   * Defines the justify-content style property
   */
  justifyContent: PropTypes.oneOf([
    "flex-start",
    "center",
    "flex-end",
    "space-between",
    "space-around",
    "space-evenly",
  ]),
  /**
   * Defines the align-items style property
   */
  alignItems: PropTypes.oneOf([
    "flex-start",
    "center",
    "flex-end",
    "stretch",
    "baseline",
  ]),
  /**
   * The component used for the root node
   */
  component: PropTypes.elementType,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles
   */
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])
    ),
    PropTypes.func,
    PropTypes.object,
  ]),
};

// No defaultProps - inherit from underlying Grid behavior

export default HGrid;

